import { create } from "zustand";
import type { ConnectorSide, StampKind, TextAlign } from "@refinement-canvas/domain";
import type { ViewState } from "../../../lib/canvas-coordinates";
import type { MarqueeRect } from "../../../lib/marquee";
import { loadGridSnapPreference, saveGridSnapPreference } from "../../../lib/grid-snap-preference";
import { loadPenColor, type PenColor } from "../../../lib/pen-preference";
import { loadStickyColor, type StickyColor } from "../../../lib/sticky-preference";
import { loadTextFontSize, saveTextFontSize } from "../../../lib/text-size-preference";
import { loadTextAlign, saveTextAlign } from "../../../lib/text-align-preference";

// キャンバス UI のローカル状態を管理する Zustand store。
// pan/zoom、選択中ツール、選択中オブジェクトなど高頻度更新の値を保持する。

export type CanvasTool =
  | "idle"
  | "panning"
  | "place-sticky"
  | "place-shape"
  | "place-text"
  | "draw-pen"
  | "place-stamp";

export type ShapeKind = "circle" | "rectangle" | "arrow";

export interface PointerPosition {
  readonly x: number;
  readonly y: number;
}

/** 右クリックで開くコンテキストメニューの状態（クライアント座標）。 */
export interface ContextMenuState {
  readonly objectId: string;
  readonly clientX: number;
  readonly clientY: number;
}

/**
 * コネクタ（接続矢印）作成中の一時状態。
 * 接続元（fromObjectId / fromSide）からポインタ位置（ステージ座標）へゴースト線を引き、
 * 接続先候補が見つかれば target に保持して 4 辺ドットを表示・スナップする。
 */
export interface ConnectionDraftState {
  readonly fromObjectId: string;
  readonly fromSide: ConnectorSide;
  readonly pointer: PointerPosition;
  readonly target: { readonly objectId: string; readonly side: ConnectorSide } | null;
}

interface CanvasUiState {
  view: ViewState;
  tool: CanvasTool;
  shapeKind: ShapeKind;
  /** スタンプ配置時に使う絵文字種別。place-stamp ツール時のサブ選択結果を保持する。 */
  stampKind: StampKind;
  penColor: PenColor;
  stickyColor: StickyColor;
  /** テキスト配置で使用する初期 font-size（px 値）。プリセット（小/中/大）・px 指定の両方を保持する。 */
  textFontSize: number;
  /** テキスト・付箋配置で使用する初期の文字寄せ（左/中央/右）。既定は左寄せ。 */
  textAlign: TextAlign;
  selectedObjectIds: readonly string[];
  /**
   * 範囲指定（マーキー）選択のドラッグ中に表示する選択枠（ステージ座標）。
   * ドラッグしていないときは null。確定時に枠へ重なるオブジェクトを選択する。
   */
  marqueeRect: MarqueeRect | null;
  lastPointerStagePosition: PointerPosition | null;
  /** グリッド吸着モード（ON のとき、ペン以外のオブジェクトは確定時に CANVAS_SNAP_PX へ丸める）。 */
  gridSnapEnabled: boolean;
  contextMenu: ContextMenuState | null;
  /** コネクタ作成中の一時状態。作成していないときは null。 */
  connectionDraft: ConnectionDraftState | null;

  setView: (view: ViewState) => void;
  setTool: (tool: CanvasTool) => void;
  setShapeKind: (shape: ShapeKind) => void;
  setStampKind: (kind: StampKind) => void;
  setPenColor: (color: PenColor) => void;
  setStickyColor: (color: StickyColor) => void;
  setTextFontSize: (px: number) => void;
  setTextAlign: (align: TextAlign) => void;
  setSelectedObjectIds: (ids: readonly string[]) => void;
  setMarqueeRect: (rect: MarqueeRect | null) => void;
  setLastPointerStagePosition: (point: PointerPosition | null) => void;
  setGridSnapEnabled: (enabled: boolean) => void;
  openContextMenu: (state: ContextMenuState) => void;
  closeContextMenu: () => void;
  setConnectionDraft: (draft: ConnectionDraftState | null) => void;
  resetMode: () => void;
}

export const useCanvasStore = create<CanvasUiState>((set) => ({
  view: { panX: 0, panY: 0, zoom: 1 },
  tool: "idle",
  shapeKind: "rectangle",
  // スタンプは最初の選択肢を既定にしておく（明示的に切替するまで「いいね」スタンプが配置される）。
  stampKind: "thumbs-up",
  penColor: loadPenColor(),
  stickyColor: loadStickyColor(),
  textFontSize: loadTextFontSize(),
  textAlign: loadTextAlign(),
  selectedObjectIds: [],
  marqueeRect: null,
  lastPointerStagePosition: null,
  gridSnapEnabled: loadGridSnapPreference(),
  contextMenu: null,
  connectionDraft: null,

  setView: (view) => set({ view }),
  setTool: (tool) => set({ tool }),
  setShapeKind: (shapeKind) => set({ shapeKind }),
  setStampKind: (stampKind) => set({ stampKind }),
  setPenColor: (penColor) => set({ penColor }),
  setStickyColor: (stickyColor) => set({ stickyColor }),
  setTextFontSize: (px) => {
    // ローカルストレージにも永続化し、次回の挿入時に再現できるようにする。
    saveTextFontSize(px);
    set({ textFontSize: px });
  },
  setTextAlign: (align) => {
    // 文字サイズと同様、次回の挿入時に再現できるようローカルストレージへ永続化する。
    saveTextAlign(align);
    set({ textAlign: align });
  },
  setSelectedObjectIds: (selectedObjectIds) => set({ selectedObjectIds }),
  setMarqueeRect: (marqueeRect) => set({ marqueeRect }),
  setLastPointerStagePosition: (lastPointerStagePosition) => set({ lastPointerStagePosition }),
  setGridSnapEnabled: (enabled) => {
    saveGridSnapPreference(enabled);
    set({ gridSnapEnabled: enabled });
  },
  openContextMenu: (contextMenu) => set({ contextMenu }),
  closeContextMenu: () => set({ contextMenu: null }),
  setConnectionDraft: (connectionDraft) => set({ connectionDraft }),
  resetMode: () =>
    set({
      tool: "idle",
      selectedObjectIds: [],
      contextMenu: null,
      marqueeRect: null,
      connectionDraft: null,
    }),
}));
